import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Home,
  Users,
  Building2,
  Upload,
  ArrowLeft,
  Settings,
  BookMarked,
  Bookmark,
  Monitor,
} from "lucide-react";
import { Button } from "./ui/button";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { clientService } from "../services/clientService";
import s3UploadService from "../services/s3UploadService";
import toast from "react-hot-toast";
import {
  validateClientName,
  CLIENT_NAME_MAX_LENGTH,
  CLIENT_DESCRIPTION_MAX_LENGTH,
  TRADEMARK_MAX_LENGTH,
  WEB_ADDRESS_MIN_LENGTH,
  WEB_ADDRESS_MAX_LENGTH,
  validateClientDescription,
  validateTrademark,
  validateWebAddress,
  BILLING_ZIP_POSTAL_CODE_PATTERN,
  BILLING_ZIP_CODE_ERROR,
} from "../utils/validationUtils";
import ClientConfigurationStep from "./ClientConfigurationStep";
import SearchableISDSelect from "./SearchableISDSelect";
import SearchableTimezoneSelect from "./SearchableTimezoneSelect";
import { findCountryByDialCode } from "../data/countryIsdCodes";
import Stack from "@mui/material/Stack";
import {
  getDefaultBrowserTimezone,
  getUtcOffsetLabel,
} from "../utils/timezoneOptions";

const WEB_ADDRESS_SUFFIX = ".testnimbus.io";

const ADMIN_MOBILE_MIN_DIGITS = 8;
const ADMIN_MOBILE_MAX_DIGITS = 12;

/** Must match the Authorised representative department <select> option values */
const ADMIN_DEPARTMENT_OPTIONS = ["QA", "Product", "Dev", "Support"];

/**
 * Map API / DB department to a select value. HTML selects need an exact value match;
 * user service may return different casing (e.g. "qa"), which would otherwise show blank.
 */
function normalizeAdminDepartmentForSelect(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  const match = ADMIN_DEPARTMENT_OPTIONS.find(
    (opt) => opt.toLowerCase() === s.toLowerCase(),
  );
  return match ?? "";
}

const sanitizeAdminMobileInput = (raw) =>
  String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, ADMIN_MOBILE_MAX_DIGITS);

const validateClientNameFormat = validateClientName;

/** Normalize API / legacy values to form values TEST | Production */
const normalizeClientTypeForForm = (v) => {
  if (!v || typeof v !== "string") return "";
  const u = v.trim().toUpperCase();
  if (u === "TEST") return "TEST";
  if (u === "PROD" || u === "PRODUCTION") return "Production";
  const lower = v.trim().toLowerCase();
  if (lower === "test") return "TEST";
  if (lower === "production" || lower === "prod") return "Production";
  return "";
};

const STORAGE_KEY = "client_creation_wizard_state_v3";

/** Red asterisk for mandatory fields */
const RequiredStar = () => <span className="text-red-500">*</span>;

const BILLING_ZIP_MIN = 3;
const BILLING_ZIP_MAX = 10;
const BILLING_ZIP_PATTERN = /^[A-Za-z0-9]+$/;

const sanitizeBillingZipInput = (value) =>
  String(value ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, BILLING_ZIP_MAX);

const getBillingZipError = (raw) => {
  const z = String(raw ?? "").trim();
  if (!z) return "Zip/Postal code is required";
  if (z.length < BILLING_ZIP_MIN || z.length > BILLING_ZIP_MAX) {
    return `Zip/Postal code must be ${BILLING_ZIP_MIN}–${BILLING_ZIP_MAX} characters (letters and numbers only)`;
  }
  if (!BILLING_ZIP_PATTERN.test(z)) {
    return "Zip/Postal code may only contain letters and numbers";
  }
  return null;
};

const ClientCreationWizard = ({
  isOpen,
  onClose,
  onSuccess,
  fullPage = false,
  viewMode = false,
  clientId = null,
  /** When true (All Clients → Edit), Save/Cancel navigate to /clients and do not switch global client context */
  returnToAllClients = false,
}) => {
  console.log({ viewMode, isOpen });
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [validatingName, setValidatingName] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // =========================
  // Step 1: Client Details
  // =========================
  const [clientName, setClientName] = useState("");
  /** Server-loaded name when editing; used to skip validate-name API if unchanged */
  const [initialClientName, setInitialClientName] = useState("");
  const [clientDescription, setClientDescription] = useState("");
  const [clientNameError, setClientNameError] = useState("");
  const [clientDescriptionError, setClientDescriptionError] = useState("");
  const [clientTradeMarkError, setClientTradeMarkError] = useState("");
  const [webAddressError, setWebAddressError] = useState("");
  const [webAddress, setWebAddress] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [clientTradeMark, setClientTradeMark] = useState("");
  const [webAddressPrefix, setWebAddressPrefix] = useState(""); // user types prefix; full = prefix + .testnimbus.io
  const [companyWebAddress, setCompanyWebAddress] = useState("");
  const [companyWebAddressError, setCompanyWebAddressError] = useState("");
  const [countryISDCode, setCountryISDCode] = useState("");
  const [countryISDCodeError, setCountryISDCodeError] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyEmailError, setCompanyEmailError] = useState("");
  const [clientType, setClientType] = useState("Production"); // TEST / Production
  const [clientTypeError, setClientTypeError] = useState("");
  const [timezone, setTimezone] = useState(() => getDefaultBrowserTimezone());
  const [clientLogo, setClientLogo] = useState(null); // File
  const clientLogoInputRef = useRef(null);

  // =========================
  // Step 1: Admin User
  // =========================
  const [adminUser, setAdminUser] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
  });
  const [adminUserErrors, setAdminUserErrors] = useState({});

  // =========================
  // Step 1: Billing Address (SEPARATE)
  // =========================
  const [billingAddress, setBillingAddress] = useState({
    address: "",
    country: "",
    state: "",
    city: "",
    zip_code: "",
  });
  const [billingAddressErrors, setBillingAddressErrors] = useState({});

  // =========================
  // Step 2: Client Configuration
  // =========================
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading] = useState(false);
  const [configurationSaved, setConfigurationSaved] = useState(false);

  // =========================
  // Load saved state
  // =========================
  const loadClient = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await clientService.getClientById(clientId);
      if (response.success) {
        const clientData = response.data || response;
        console.log({ clientData });
        // Always open Settings → Client Details on step 1
        setCurrentStep(1);
        const nameFromServer = String(clientData.client_name || "").trim();
        setClientName(clientData.client_name || "");
        setInitialClientName(nameFromServer);
        setClientDescription(clientData.client_description || "");

        // Client details can come from legacy top-level columns or from client_details JSON
        const cd = clientData.client_details || {};
        setClientTradeMark(
          cd.client_trade_mark || clientData.client_trade_mark || "",
        );
        const fullWebAddress = cd.web_address || clientData.web_address || "";
        setWebAddress(fullWebAddress);
        setWebAddressPrefix(
          fullWebAddress && typeof fullWebAddress === "string"
            ? fullWebAddress.replace(WEB_ADDRESS_SUFFIX, "")
            : "",
        );
        setCompanyType(cd.company_type || clientData.company_type || "");
        const rawIsd = cd.country_isd_code || clientData.country_isd_code || "";
        const isdMatch = findCountryByDialCode(rawIsd);
        setCountryISDCode(
          isdMatch ? isdMatch.dial_code : String(rawIsd).trim(),
        );
        setCompanyEmail(cd.company_email || "");
        setCompanyWebAddress(cd.company_web_address || "");
        setClientType(normalizeClientTypeForForm(clientData.client_type) || "");
        setTimezone(
          clientData.timezone && String(clientData.timezone).trim()
            ? String(clientData.timezone).trim()
            : getDefaultBrowserTimezone(),
        );

        setClientLogo(clientData.logo_url); // cannot restore File

        // Authorised representative (admin user): prefer admin_user attached by backend.
        // Fallback to any legacy storage where full_name/email were stored in client_details.
        const adminFromApi = clientData.admin_user;
        const legacyAdminFromCd = cd && (cd.full_name || cd.email) ? cd : null;
        setAdminUser(
          adminFromApi
            ? {
                full_name: adminFromApi.full_name || "",
                email: adminFromApi.email || "",
                mobile_number: sanitizeAdminMobileInput(
                  adminFromApi.mobile_number || adminFromApi.mobile || "",
                ),
              }
            : legacyAdminFromCd || {
                full_name: "",
                email: "",
                mobile_number: "",
              },
        );

        const ba = clientData.billing_address_details || {};
        const rawZip = ba.zip_postal_code || ba.zip_code || "";
        setBillingAddress({
          address: ba.address || "",
          country: ba.country_region || ba.country || "",
          state: ba.state || "",
          city: ba.city || "",
          zip_code: sanitizeBillingZipInput(rawZip),
        });
      }
    } catch (error) {
      console.error("Error loading client:", error);
      toast.error("Failed to load client details");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  console.log({ clientName, adminUser, billingAddress });

  useEffect(() => {
    if (isOpen) {
      if (viewMode) {
        // Settings should open Client Details in VIEW mode by default.
        // Editing is enabled only after clicking the Edit button.
        setIsEditing(false);
        setCurrentStep(1);
        loadClient();
      } else {
        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          try {
            const state = JSON.parse(savedState);

            setCurrentStep(state.currentStep || 1);
            setClientName(state.clientName || "");
            setClientDescription(state.clientDescription || "");
            setClientTradeMark(state.clientTradeMark || "");
            setWebAddressPrefix(
              state.webAddressPrefix ??
                state.webAddress?.replace(WEB_ADDRESS_SUFFIX, "") ??
                "",
            );
            setCompanyWebAddress(state.companyWebAddress || "");
            {
              const raw = state.countryISDCode || "";
              const m = findCountryByDialCode(raw);
              setCountryISDCode(m ? m.dial_code : String(raw).trim());
            }
            setCompanyEmail(state.companyEmail || "");
            setClientType(normalizeClientTypeForForm(state.clientType) || "");
            if (state.timezone && String(state.timezone).trim()) {
              setTimezone(String(state.timezone).trim());
            }
            setClientLogo(null); // cannot restore File

            {
              const rawAdmin = state.adminUser || {
                full_name: "",
                email: "",
                mobile_number: "",
              };
              setAdminUser({
                ...rawAdmin,
                department: normalizeAdminDepartmentForSelect(
                  rawAdmin.department,
                ),
                mobile_number: sanitizeAdminMobileInput(rawAdmin.mobile_number),
              });
            }

            const restoredBa = state.billingAddress || {
              address: "",
              country: "",
              state: "",
              city: "",
              zip_code: "",
            };
            setBillingAddress({
              ...restoredBa,
              zip_code: sanitizeBillingZipInput(restoredBa.zip_code || ""),
            });

            setPermissions(state.permissions || {});
            setConfigurationSaved(state.configurationSaved || false);
          } catch (error) {
            console.error("Error loading saved state:", error);
          }
        }
      }
    }
  }, [isOpen, viewMode, clientId, loadClient]);

  // =========================
  // Save state
  // =========================
  const saveState = () => {
    const state = {
      currentStep,
      clientName,
      clientDescription,
      clientTradeMark,
      webAddressPrefix,
      companyWebAddress,
      countryISDCode,
      companyEmail,
      clientType,
      timezone,
      // clientLogo not saved
      adminUser,
      billingAddress,
      permissions,
      configurationSaved,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  const clearState = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // =========================
  // Step 1: collect all sync validation errors (no setState) so we can show every field at once
  // =========================
  const collectStep1SyncErrors = () => {
    const e = {
      clientName: "",
      clientType: "",
      clientDescription: "",
      clientTradeMark: "",
      webAddress: "",
      companyWebAddress: "",
      companyEmail: "",
      countryISDCode: "",
      adminUser: {},
      billingAddress: {},
    };

    if (!clientName.trim()) {
      e.clientName = "Client name is required";
    } else {
      const ne = validateClientNameFormat(clientName);
      if (ne) e.clientName = ne;
    }

    if (!clientType || !clientType.trim()) {
      e.clientType = "Client type is required";
    }

    const descError = validateClientDescription(clientDescription);
    if (descError) e.clientDescription = descError;

    const tmError = validateTrademark(clientTradeMark);
    if (tmError) e.clientTradeMark = tmError;

    const prefix = (webAddressPrefix || "").trim();
    const full = prefix + WEB_ADDRESS_SUFFIX;
    if (!prefix) {
      e.webAddress = "Web address is required";
    } else if (prefix.length < 6) {
      e.webAddress = "Web address must be at least 6 characters (e.g. myname)";
    } else {
      const wErr = validateWebAddress(full);
      if (wErr) e.webAddress = wErr;
    }

    if (companyWebAddress.trim()) {
      const cwa = validateWebAddress(companyWebAddress);
      if (cwa) e.companyWebAddress = cwa;
    }

    if (!companyEmail.trim()) {
      e.companyEmail = "Company email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail.trim())) {
      e.companyEmail = "Invalid email format";
    }

    if (!countryISDCode.trim()) {
      e.countryISDCode = "Country ISD code is required";
    }

    const adminErrs = {};
    if (!adminUser.full_name.trim()) {
      adminErrs.full_name = "Full name is required";
    }
    if (!adminUser.email.trim()) {
      adminErrs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminUser.email)) {
      adminErrs.email = "Invalid email format";
    }
    const allowedDepartments = ["QA", "Product", "Dev", "Support"];
    if (
      adminUser.department &&
      !allowedDepartments.includes(adminUser.department)
    ) {
      adminErrs.department =
        "Department must be one of: QA, Product, Dev, Support";
    }
    const mobileDigits = sanitizeAdminMobileInput(adminUser.mobile_number);
    if (
      mobileDigits.length > 0 &&
      mobileDigits.length < ADMIN_MOBILE_MIN_DIGITS
    ) {
      adminErrs.mobile_number = `Mobile number must be ${ADMIN_MOBILE_MIN_DIGITS}–${ADMIN_MOBILE_MAX_DIGITS} digits`;
    }
    e.adminUser = adminErrs;

    const trim = (v) => (v == null || v === undefined ? "" : String(v).trim());
    const ba = {};
    if (!trim(billingAddress.address)) ba.address = "Address is required";
    if (!trim(billingAddress.country))
      ba.country = "Country/Region is required";
    if (!trim(billingAddress.state)) ba.state = "State is required";
    if (!trim(billingAddress.city)) ba.city = "City is required";
    if (!trim(billingAddress.zip_code)) {
      ba.zip_code = "Zip/Postal code is required";
    } else if (
      !BILLING_ZIP_POSTAL_CODE_PATTERN.test(trim(billingAddress.zip_code))
    ) {
      ba.zip_code = BILLING_ZIP_CODE_ERROR;
    }
    e.billingAddress = ba;

    return e;
  };

  const applyStep1Errors = (errors) => {
    setClientNameError(errors.clientName || "");
    setClientTypeError(errors.clientType || "");
    setClientDescriptionError(errors.clientDescription || "");
    setClientTradeMarkError(errors.clientTradeMark || "");
    setWebAddressError(errors.webAddress || "");
    setCompanyWebAddressError(errors.companyWebAddress || "");
    setCompanyEmailError(errors.companyEmail || "");
    setCountryISDCodeError(errors.countryISDCode || "");
    setAdminUserErrors(
      errors.adminUser && Object.keys(errors.adminUser).length
        ? errors.adminUser
        : {},
    );
    setBillingAddressErrors(
      errors.billingAddress && Object.keys(errors.billingAddress).length
        ? errors.billingAddress
        : {},
    );
  };

  const hasStep1Errors = (e) => {
    if (e.clientName) return true;
    if (e.clientType) return true;
    if (e.clientDescription) return true;
    if (e.clientTradeMark) return true;
    if (e.webAddress) return true;
    if (e.companyWebAddress) return true;
    if (e.companyEmail) return true;
    if (e.countryISDCode) return true;
    if (e.adminUser && Object.keys(e.adminUser).length) return true;
    if (e.billingAddress && Object.keys(e.billingAddress).length) return true;
    return false;
  };

  /** Returns true if step 1 is valid, false if any error (all errors shown at once). */
  const runStep1Validation = async () => {
    const sync = collectStep1SyncErrors();

    if (!sync.clientName) {
      setValidatingName(true);
      try {
        const response = await clientService.validateClientName(
          clientName.trim(),
        );
        if (!response.data?.isUnique) {
          sync.clientName = "Client name already exists";
        }
      } catch (error) {
        console.error("Error validating client name:", error);
        toast.error("Failed to validate client name. Please try again.");
        applyStep1Errors(sync);
        return false;
      } finally {
        setValidatingName(false);
      }
    }

    applyStep1Errors(sync);
    return !hasStep1Errors(sync);
  };

  // =========================
  // Step 1 Next
  // =========================
  const handleStep1Next = async () => {
    const ok = await runStep1Validation();
    if (!ok) return;
    saveState();
    setCurrentStep(2);
  };

  // =========================
  // Step 2 Save config
  // =========================
  const handleStep2Save = async (permissionsData) => {
    setPermissions(permissionsData);
    setConfigurationSaved(true);
    saveState();
    toast.success("Configuration ready");
  };

  const handleSavedStateChange = (isSaved) => {
    setConfigurationSaved(isSaved);
    saveState();
  };

  // =========================
  // Finish
  // =========================

  const handleUpdate = async () => {
    const ok = await runStep1Validation();
    if (!ok) return;

    let logoUrl = "";
    if (clientLogo && clientId) {
      try {
        logoUrl = await s3UploadService.uploadFile(
          clientLogo,
          "client-logos",
          clientId,
        );
      } catch (logoError) {
        console.error("Error uploading client logo:", logoError);
        toast.error(
          "Client created but logo upload failed. You can add the logo later from client settings.",
        );
      }
    }

    setLoading(true);
    try {
      const payload = {
        client_name: clientName?.trim(),
        client_description: clientDescription?.trim() || null,
        client_trade_mark: clientTradeMark?.trim() || null,
        web_address: webAddressPrefix?.trim()
          ? webAddressPrefix?.trim() + WEB_ADDRESS_SUFFIX
          : null,
        company_type: companyType?.trim() || null,
        country_isd_code: countryISDCode?.trim() || null,
        client_type: clientType || null,
        company_email: companyEmail?.trim() || null,
        timezone: timezone?.trim() || getDefaultBrowserTimezone(),

        admin_user: {
          full_name: adminUser.full_name?.trim(),
          email: adminUser.email?.trim()?.toLowerCase(),
          mobile_number:
            sanitizeAdminMobileInput(adminUser.mobile_number) || null,
        },
        // authorised representative is shown from user-service; keep payload minimal

        billing_address: {
          address: billingAddress.address?.trim() || null,
          country: billingAddress.country?.trim() || null,
          state: billingAddress.state?.trim() || null,
          city: billingAddress.city?.trim() || null,
          zip_code: billingAddress.zip_code?.trim() || null,
        },
        // Do not send permissions here — CLIENT_CONFIGURATION is updated from the
        // configuration step / Client Configuration page, not this save.
      };
      if (logoUrl) payload["logo_url"] = logoUrl;
      const response = await clientService.updateClient(clientId, payload);
      if (response.success) {
        toast.success("Client updated successfully!");
        setIsEditing(false);
        if (onSuccess) onSuccess(response.data);
        if (returnToAllClients) {
          navigate("/clients");
          return;
        }
        loadClient();
      } else {
        toast.error(response.message || "Failed to create client");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update client");
    }
  };
  const handleFinish = async () => {
    if (!configurationSaved) {
      toast.error("Please save the client configuration first");
      return;
    }

    const ok = await runStep1Validation();
    if (!ok) {
      setCurrentStep(1);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_name: clientName.trim(),
        client_description: clientDescription.trim() || null,
        client_trade_mark: clientTradeMark.trim() || null,
        web_address: webAddressPrefix.trim()
          ? webAddressPrefix.trim() + WEB_ADDRESS_SUFFIX
          : null,
        country_isd_code: countryISDCode.trim() || null,
        company_email: companyEmail.trim() || null,
        client_type: clientType || null,
        timezone: timezone?.trim() || getDefaultBrowserTimezone(),

        admin_user: {
          full_name: adminUser.full_name.trim(),
          email: adminUser.email.trim().toLowerCase(),
          mobile_number:
            sanitizeAdminMobileInput(adminUser.mobile_number) || null,
        },

        billing_address: {
          address: billingAddress.address.trim() || null,
          country: billingAddress.country.trim() || null,
          state: billingAddress.state.trim() || null,
          city: billingAddress.city.trim() || null,
          zip_code: billingAddress.zip_code.trim() || null,
        },

        permissions,
      };

      const response = await clientService.completeClientWizard(payload);

      if (response.success) {
        const createdClient = response.data;
        const clientId = createdClient?.client_id;

        // Upload logo if one was selected (client must exist first to get client_id)
        if (clientLogo && clientId) {
          try {
            const logoUrl = await s3UploadService.uploadFile(
              clientLogo,
              "client-logos",
              clientId,
            );
            await clientService.updateClient(clientId, {
              client_name: clientName.trim(),
              client_description: clientDescription.trim() || null,
              logo_url: logoUrl,
            });
          } catch (logoError) {
            console.error("Error uploading client logo:", logoError);
            toast.error(
              "Client created but logo upload failed. You can add the logo later from client settings.",
            );
          }
        }

        clearState();
        toast.success("Client created successfully!");
        if (onSuccess) onSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || "Failed to create client");
      }
    } catch (error) {
      console.error("Error completing client wizard:", error);
      toast.error(error.response?.data?.message || "Failed to create client");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      saveState();
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All unsaved progress will be lost.",
      )
    ) {
      clearState();
      onClose();
    }
  };

  const handleReset = () => {
    if (window.confirm("Reset all fields to default?")) {
      clearState();
      setCurrentStep(1);

      setClientName("");
      setClientDescription("");
      setClientNameError("");
      setClientDescriptionError("");
      setClientTradeMarkError("");
      setWebAddressError("");

      setClientTradeMark("");
      setWebAddressPrefix("");
      setCompanyWebAddress("");
      setCountryISDCode("");
      setCountryISDCodeError("");
      setCompanyEmail("");
      setCompanyWebAddressError("");
      setCompanyEmailError("");
      setClientType("");
      setTimezone(getDefaultBrowserTimezone());
      setClientLogo(null);

      setAdminUser({ full_name: "", email: "", mobile_number: "" });
      setAdminUserErrors({});
      setBillingAddressErrors({});

      setBillingAddress({
        address: "",
        country: "",
        state: "",
        city: "",
        zip_code: "",
      });

      setPermissions({});
      setConfigurationSaved(false);
    }
  };

  if (!isOpen && !fullPage) return null;

  // Determine if fields should be disabled
  const fieldsDisabled = viewMode && !isEditing;

  // In Settings (viewMode + embedded in SettingsPage card), avoid nesting another "modal-like" card.
  const contentWrapperClass =
    viewMode && !fullPage
      ? "bg-transparent shadow-none border-0 rounded-none w-full flex flex-col"
      : fullPage
        ? "card flex flex-col flex-1 min-h-0"
        : "card w-full max-h-[90vh] overflow-hidden flex flex-col";

  const mainContent = (
    <div className={contentWrapperClass}>
      {/* HEADER */}
      {!viewMode && (
        <div
          className="border-b"
          style={{
            padding: "18px 20px",
            background: "#fff",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {fullPage && (
                <button
                  type="button"
                  onClick={() => (onClose ? onClose() : navigate("/clients"))}
                  className="flex items-center justify-center w-9 h-9 rounded-md border border-border-primary bg-background text-text-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label="Back to clients"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-3xl font-semibold">
                  {viewMode ? "Client Details" : "Add New Client"}
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Create client to step into the World of Testing.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!viewMode && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset to Default
                </Button>
              )}
            </div>
          </div>

          {/* STEP PILLS */}
          <div className="flex items-center justify-center mt-6 gap-6">
            <div
              className="flex items-center justify-center gap-2"
              style={{
                width: "260px",
                height: "38px",
                borderRadius: "999px",
                background: currentStep === 1 ? "#2E2A2B" : "transparent",
                border: currentStep === 1 ? "none" : "1px solid #CFCFCF",
                color: currentStep === 1 ? "white" : "#222",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              <Bookmark />
              Basic Details
            </div>

            <div
              style={{ width: "240px", height: "1px", background: "#D6D6D6" }}
            />

            <div
              className="flex items-center justify-center gap-2"
              style={{
                width: "260px",
                height: "38px",
                borderRadius: "999px",
                background: currentStep === 2 ? "#2E2A2B" : "transparent",
                border: currentStep === 2 ? "none" : "1px solid #CFCFCF",
                color: currentStep === 2 ? "white" : "#222",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              <Settings className="w-4 h-4" />
              User Configuration
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODE HEADER (sticky actions so Edit is always visible) */}
      {viewMode && (
        <div className="border-b border-border-primary bg-bg-primary sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h2 className="text-2xl font-semibold">Client Details</h2>
              <p className="text-text-secondary text-sm mt-1">
                View client configuration. Click Edit to make changes.
              </p>
            </div>

            <div className="flex items-center gap-2">
              {!isEditing ? (
                <Button variant="default" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (returnToAllClients) {
                        navigate("/clients");
                        return;
                      }
                      setIsEditing(false);
                      await loadClient();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleUpdate}
                    disabled={loading || permissionsLoading}
                  >
                    {loading ? (
                      <>
                        <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "22px" }}>
        <fieldset
          disabled={fieldsDisabled}
          className="min-w-0"
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          {/* STEP 1 */}
          {currentStep === 1 && (
            <div className="space-y-8">
              {/* General Details */}
              <div className="bg-bg-tertiary rounded-[14px] p-5">
                <Stack flexDirection={"row"} gap={"4px"}>
                  <Home className="w-6 h-6" style={{ color: "#e8618c" }} />{" "}
                  <h3 className="text-lg font-semibold mb-4">
                    General Details
                  </h3>
                </Stack>

                <div
                  className="grid gap-x-6 gap-y-5"
                  style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                    alignItems: "start",
                  }}
                >
                  {/* Field wrapper: fixed label row height + fixed input height for alignment */}
                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex justify-between items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">
                        Client Name <RequiredStar />
                      </Label>
                      <span
                        className={`text-sm tabular-nums ${clientName.length > CLIENT_NAME_MAX_LENGTH ? "text-red-500" : "text-gray-500"}`}
                      >
                        {clientName.length}/{CLIENT_NAME_MAX_LENGTH}
                      </span>
                    </div>
                    <Input
                      value={clientName}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.length <= CLIENT_NAME_MAX_LENGTH)
                          setClientName(v);
                        setClientNameError("");
                      }}
                      placeholder="Client Name (max 50 characters)"
                      maxLength={CLIENT_NAME_MAX_LENGTH}
                      className={clientNameError ? "border-red-300" : ""}
                      disabled={fieldsDisabled || loading || validatingName}
                    />
                    {clientNameError && (
                      <p className="text-red-500 text-sm mt-1">
                        {clientNameError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex justify-between items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">Client Trade Mark</Label>
                      <span
                        className={`text-sm tabular-nums ${clientTradeMark.length > TRADEMARK_MAX_LENGTH ? "text-red-500" : "text-gray-500"}`}
                      >
                        {clientTradeMark.length}/{TRADEMARK_MAX_LENGTH}
                      </span>
                    </div>
                    <Input
                      value={clientTradeMark}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.length <= TRADEMARK_MAX_LENGTH)
                          setClientTradeMark(v);
                        setClientTradeMarkError("");
                      }}
                      placeholder="Trademark (max 50 characters)"
                      maxLength={TRADEMARK_MAX_LENGTH}
                      className={clientTradeMarkError ? "border-red-300" : ""}
                      disabled={fieldsDisabled || loading}
                    />
                    {clientTradeMarkError && (
                      <p className="text-red-500 text-sm mt-1">
                        {clientTradeMarkError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">
                        Web Address <RequiredStar />
                      </Label>
                    </div>
                    <div
                      className={`flex h-10 rounded-md border overflow-hidden flex-shrink-0 ${webAddressError ? "border-red-500 ring-2 ring-red-500 ring-opacity-50" : "border-input"}`}
                      style={{ background: "#fff" }}
                    >
                      <input
                        type="text"
                        value={webAddressPrefix}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (
                            v.length <=
                            WEB_ADDRESS_MAX_LENGTH - WEB_ADDRESS_SUFFIX.length
                          )
                            setWebAddressPrefix(v);
                          setWebAddressError("");
                        }}
                        placeholder="choose a name"
                        disabled={fieldsDisabled || loading || isEditing}
                        className="flex-1 min-w-0 h-full px-3 text-sm border-0 focus:ring-0 focus:outline-none bg-transparent disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100"
                      />
                      <span
                        className="inline-flex items-center h-full px-3 text-sm text-gray-700 whitespace-nowrap border-l border-gray-200"
                        style={{ background: "#f4f4f5" }}
                      >
                        {WEB_ADDRESS_SUFFIX}
                      </span>
                    </div>
                    {webAddressError && (
                      <p className="text-red-500 text-sm mt-1">
                        {webAddressError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">Client Logo</Label>
                    </div>
                    <div className="h-10 flex items-center flex-shrink-0">
                      <input
                        ref={clientLogoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setClientLogo(e.target.files?.[0] || null)
                        }
                        className="hidden"
                        disabled={fieldsDisabled || loading}
                        aria-hidden
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 px-4"
                        onClick={() => clientLogoInputRef.current?.click()}
                        disabled={fieldsDisabled || loading}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {clientLogo ? "Change file" : "Choose file"}
                      </Button>
                    </div>
                    {clientLogo && (
                      <p
                        className="text-xs text-gray-600 mt-1"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "200px",
                        }}
                      >
                        Selected:{" "}
                        {typeof clientLogo === "string"
                          ? clientLogo
                          : clientLogo.name}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">
                        Country ISD code <RequiredStar />
                      </Label>
                    </div>
                    <div className="h-10 flex-shrink-0">
                      <SearchableISDSelect
                        value={countryISDCode}
                        onChange={(v) => {
                          setCountryISDCode(v);
                          setCountryISDCodeError("");
                        }}
                        disabled={fieldsDisabled || loading || isEditing}
                        className="disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100"
                        placeholder="Search or select country code"
                      />
                    </div>
                    {countryISDCodeError && (
                      <p className="text-red-500 text-sm mt-1">
                        {countryISDCodeError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">
                        Company Email <RequiredStar />
                      </Label>
                    </div>
                    <Input
                      type="email"
                      value={companyEmail}
                      onChange={(e) => {
                        setCompanyEmail(e.target.value);
                        setCompanyEmailError("");
                      }}
                      placeholder="Company email"
                      disabled={loading}
                      className={`h-10 ${companyEmailError ? "border-red-500 ring-2 ring-red-500 ring-opacity-50" : ""}`}
                    />
                    {companyEmailError && (
                      <p className="text-red-500 text-sm mt-1">
                        {companyEmailError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col min-h-[72px]">
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">
                        Client Type <RequiredStar />
                      </Label>
                    </div>
                    <select
                      value={clientType}
                      onChange={(e) => {
                        setClientType(e.target.value);
                        setClientTypeError("");
                      }}
                      disabled={fieldsDisabled || loading || isEditing}
                      className={`flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 ${clientTypeError ? "border-red-300 ring-2 ring-red-500 ring-opacity-50" : "border-input"}`}
                    >
                      <option value="">Select</option>
                      <option value="TEST">Test</option>
                      <option value="Production">Production</option>
                    </select>
                    {clientTypeError && (
                      <p className="text-red-500 text-sm mt-1">
                        {clientTypeError}
                      </p>
                    )}
                  </div>

                  <div
                    className="flex flex-col min-h-[72px]"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <div className="flex items-center mb-1 min-h-[22px]">
                      <Label className="leading-tight">Timezone</Label>
                    </div>
                    <SearchableTimezoneSelect
                      value={timezone}
                      onChange={(iana) => {
                        setTimezone(iana);
                      }}
                      disabled={fieldsDisabled || loading}
                      className="bg-white disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      {getUtcOffsetLabel(timezone || "UTC")} · Current time:{" "}
                      {new Intl.DateTimeFormat(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: timezone || "UTC",
                      }).format(new Date())}
                    </p>
                  </div>
                </div>

                {/* Optional Description */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-1">
                    <Label>Client Description (Optional)</Label>
                    <span
                      className={`text-sm ${
                        clientDescription.length > CLIENT_DESCRIPTION_MAX_LENGTH
                          ? "text-red-500"
                          : "text-gray-500"
                      }`}
                    >
                      {clientDescription.length}/{CLIENT_DESCRIPTION_MAX_LENGTH}
                    </span>
                  </div>
                  <Textarea
                    value={clientDescription}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= CLIENT_DESCRIPTION_MAX_LENGTH)
                        setClientDescription(value);
                      setClientDescriptionError("");
                    }}
                    placeholder="Enter client description (max 300 characters)"
                    maxLength={CLIENT_DESCRIPTION_MAX_LENGTH}
                    rows={4}
                    className={
                      clientDescriptionError
                        ? "border-red-300 ring-2 ring-red-500 ring-opacity-50"
                        : ""
                    }
                    disabled={fieldsDisabled || loading}
                  />
                  {clientDescriptionError && (
                    <p className="text-red-500 text-sm mt-1">
                      {clientDescriptionError}
                    </p>
                  )}
                </div>
              </div>

              {/* Admin User */}
              <div className="bg-bg-tertiary rounded-[14px] p-5">
                <Stack flexDirection={"row"} gap={"4px"}>
                  <Users className="w-6 h-6" style={{ color: "#e8618c" }} />{" "}
                  <h3 className="text-lg font-semibold mb-4">
                    Authorised Representative Details (Admin User)
                  </h3>
                </Stack>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "18px",
                  }}
                >
                  <div>
                    <Label>
                      Full Name <RequiredStar />
                    </Label>
                    <Input
                      value={adminUser.full_name}
                      onChange={(e) => {
                        setAdminUser({
                          ...adminUser,
                          full_name: e.target.value,
                        });
                        setAdminUserErrors({
                          ...adminUserErrors,
                          full_name: "",
                        });
                      }}
                      placeholder="Full Name"
                      className={
                        adminUserErrors.full_name
                          ? "border-red-300 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                      disabled={loading || fieldsDisabled}
                    />
                    {adminUserErrors.full_name && (
                      <p className="text-red-500 text-sm mt-1">
                        {adminUserErrors.full_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>Mobile Number</Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={adminUser.mobile_number}
                      onChange={(e) => {
                        const digits = sanitizeAdminMobileInput(e.target.value);
                        setAdminUser({ ...adminUser, mobile_number: digits });
                        setAdminUserErrors({
                          ...adminUserErrors,
                          mobile_number: "",
                        });
                      }}
                      placeholder={`${ADMIN_MOBILE_MIN_DIGITS}–${ADMIN_MOBILE_MAX_DIGITS} digits`}
                      className={
                        adminUserErrors.mobile_number
                          ? "border-red-300 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                      disabled={loading || fieldsDisabled}
                    />
                    {adminUserErrors.mobile_number && (
                      <p className="text-red-500 text-sm mt-1">
                        {adminUserErrors.mobile_number}
                      </p>
                    )}
                  </div>

                  <div style={{ gridColumn: "1 / span 2" }}>
                    <Label>
                      Email <RequiredStar />
                    </Label>
                    <Input
                      type="email"
                      value={adminUser.email}
                      onChange={(e) => {
                        setAdminUser({ ...adminUser, email: e.target.value });
                        setAdminUserErrors({ ...adminUserErrors, email: "" });
                      }}
                      placeholder="Email"
                      className={
                        adminUserErrors.email
                          ? "border-red-300 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                      disabled={loading || fieldsDisabled}
                    />
                    {adminUserErrors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {adminUserErrors.email}
                      </p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-600 mt-4">
                  Password will be auto-generated and sent to this email.
                </p>
              </div>

              {/* Billing Address */}
              <div className="bg-bg-tertiary rounded-[14px] p-5">
                <Stack flexDirection={"row"} gap={"4px"}>
                  <Building2 className="w-6 h-6" style={{ color: "#e8618c" }} />{" "}
                  <h3 className="text-lg font-semibold mb-4">
                    Billing Address
                  </h3>
                </Stack>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "18px",
                  }}
                >
                  <div style={{ gridColumn: "1 / span 2" }}>
                    <Label>
                      Address <RequiredStar />
                    </Label>
                    <Input
                      value={billingAddress.address}
                      onChange={(e) => {
                        setBillingAddress({
                          ...billingAddress,
                          address: e.target.value,
                        });
                        setBillingAddressErrors({
                          ...billingAddressErrors,
                          address: "",
                        });
                      }}
                      placeholder="Add address"
                      disabled={loading || fieldsDisabled}
                      className={
                        billingAddressErrors.address
                          ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                    />
                    {billingAddressErrors.address && (
                      <p className="text-red-500 text-sm mt-1">
                        {billingAddressErrors.address}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>
                      Country/Region <RequiredStar />
                    </Label>
                    <Input
                      value={billingAddress.country}
                      onChange={(e) => {
                        setBillingAddress({
                          ...billingAddress,
                          country: e.target.value,
                        });
                        setBillingAddressErrors({
                          ...billingAddressErrors,
                          country: "",
                        });
                      }}
                      placeholder="Select country/region"
                      disabled={loading || fieldsDisabled}
                      className={
                        billingAddressErrors.country
                          ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                    />
                    {billingAddressErrors.country && (
                      <p className="text-red-500 text-sm mt-1">
                        {billingAddressErrors.country}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>
                      State <RequiredStar />
                    </Label>
                    <Input
                      value={billingAddress.state}
                      onChange={(e) => {
                        setBillingAddress({
                          ...billingAddress,
                          state: e.target.value,
                        });
                        setBillingAddressErrors({
                          ...billingAddressErrors,
                          state: "",
                        });
                      }}
                      placeholder="Select state"
                      disabled={loading || fieldsDisabled}
                      className={
                        billingAddressErrors.state
                          ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                    />
                    {billingAddressErrors.state && (
                      <p className="text-red-500 text-sm mt-1">
                        {billingAddressErrors.state}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>
                      City <RequiredStar />
                    </Label>
                    <Input
                      value={billingAddress.city}
                      onChange={(e) => {
                        setBillingAddress({
                          ...billingAddress,
                          city: e.target.value,
                        });
                        setBillingAddressErrors({
                          ...billingAddressErrors,
                          city: "",
                        });
                      }}
                      placeholder="Select city"
                      disabled={loading || fieldsDisabled}
                      className={
                        billingAddressErrors.city
                          ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                    />
                    {billingAddressErrors.city && (
                      <p className="text-red-500 text-sm mt-1">
                        {billingAddressErrors.city}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>
                      Zip/Postal code <RequiredStar />
                    </Label>
                    <Input
                      value={billingAddress.zip_code}
                      onChange={(e) => {
                        const zip = sanitizeBillingZipInput(e.target.value);
                        setBillingAddress({ ...billingAddress, zip_code: zip });
                        setBillingAddressErrors({
                          ...billingAddressErrors,
                          zip_code: "",
                        });
                      }}
                      placeholder="3–10 letters or numbers"
                      maxLength={BILLING_ZIP_MAX}
                      disabled={loading || fieldsDisabled}
                      className={
                        billingAddressErrors.zip_code
                          ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
                          : ""
                      }
                    />
                    {billingAddressErrors.zip_code && (
                      <p className="text-red-500 text-sm mt-1">
                        {billingAddressErrors.zip_code}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 bg-bg-tertiary rounded-[14px] px-5 py-4">
                <Settings className="w-6 h-6" style={{ color: "#e8618c" }} />
                <h3 className="text-lg font-semibold m-0">
                  User Configuration
                </h3>
              </div>
              <ClientConfigurationStep
                clientId={null}
                onSave={handleStep2Save}
                loading={permissionsLoading}
                saved={configurationSaved}
                onSavedStateChange={handleSavedStateChange}
                disabled={fieldsDisabled}
              />
            </div>
          )}
        </fieldset>
      </div>

      {/* FOOTER */}
      <div
        className="flex items-center justify-between border-t"
        style={{
          padding: "16px 18px",
          background: "#fff",
        }}
      >
        <div>
          {currentStep > 1 && !viewMode && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={loading || permissionsLoading}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {viewMode ? null : (
            // Normal Mode Buttons
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading || permissionsLoading}
              >
                Cancel
              </Button>

              {currentStep === 1 ? (
                <Button
                  onClick={handleStep1Next}
                  disabled={loading || validatingName || permissionsLoading}
                  variant="main"
                  style={{
                    background: "#2E9E6F",
                    color: "white",
                  }}
                >
                  {validatingName ? (
                    <>
                      <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                      Validating...
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleFinish}
                  variant="main"
                  disabled={
                    loading || permissionsLoading || !configurationSaved
                  }
                  style={{
                    background: "#2E9E6F",
                    color: "white",
                  }}
                >
                  {loading ? (
                    <>
                      <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Finish
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (fullPage) {
    return (
      <div className="h-[calc(100vh-5rem)] bg-gray-50 flex flex-col -m-2">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto flex flex-col h-full">{mainContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        viewMode
          ? ""
          : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      }
    >
      {mainContent}
    </div>
  );
};

export default ClientCreationWizard;
